/**
 * Custom Safari Builder Service
 * Handles itinerary generation, transfer calculation, and pricing
 */

import { Tour } from '../types';
import {
  SelectedTour,
  CustomSafariItinerary,
  Transfer,
  SafariPricing,
  PricingBreakdown,
  CustomSafariCartItem,
  ScheduledSafari
} from '../types/customSafari';

class CustomSafariService {
  private readonly TRANSFER_COST_PER_KM = 50; // KES per kilometer
  private readonly TAX_RATE = 0.16; // 16% VAT
  private readonly SERVICE_FEE = 5000; // KES fixed service fee

  // Cart caching
  private cartCache: Map<string, { items: CustomSafariCartItem[]; timestamp: number }>; // Cache configuration
  private readonly CART_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly GET_CART_FAILURE_THRESHOLD = 3;
  private readonly GET_CART_COOLDOWN_DURATION = 60 * 1000; // 1 minute

  // Circuit breaker state
  private getCartFailures = 0;
  private lastGetCartFailure = 0;

  public constructor() {
    this.cartCache = new Map();
  }

  /**
   * Generate an optimized itinerary from selected tours
   * Orders tours logically based on location and dates
   */
  async generateItinerary(tours: Tour[], name?: string, targetCurrency: string = 'USD'): Promise<CustomSafariItinerary> {
    // Sort tours by location proximity (simple heuristic - can be enhanced with actual distance calculation)
    const sortedTours = this.optimizeTourOrder(tours);

    // Create selected tours with order
    const selectedTours: SelectedTour[] = sortedTours.map((tour, index) => ({
      tour,
      order: index + 1,
      participants: 1, // Default, can be changed by user
    }));

    // Calculate transfers between tours (includes participant scaling)
    const transfers = this.calculateTransfers(selectedTours);

    // Calculate total duration
    const totalDuration = this.calculateTotalDuration(selectedTours);

    // Calculate pricing (async - includes currency conversion)
    const totalPrice = await this.calculatePricing(selectedTours, transfers, [], targetCurrency);

    // Generate itinerary
    const itinerary: CustomSafariItinerary = {
      id: `safari_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name || `Custom Safari - ${new Date().toLocaleDateString()}`,
      tours: selectedTours,
      transfers,
      totalDuration,
      totalPrice,
      generatedAt: new Date().toISOString(),
      status: 'draft',
    };

    return itinerary;
  }

  /**
   * Optimize tour order based on high-end safari company best practices:
   * 1. Group by category/experience type (Safari → Cultural → Adventure → Photography)
   * 2. Within each group, order by location proximity
   * 3. Consider logical flow (e.g., start with wildlife viewing, end with relaxation)
   */
  private optimizeTourOrder(tours: Tour[]): Tour[] {
    if (tours.length <= 1) return tours;

    // Define experience priority (lower number = earlier in itinerary)
    const experiencePriority: { [key: string]: number } = {
      'Safari': 1,
      'Wildlife': 1,
      'Cultural': 2,
      'Adventure': 3,
      'Photography': 4,
      'Honeymoon': 5,
      'Detox': 6,
    };

    // Group tours by category
    const grouped: { [key: string]: Tour[] } = {};
    tours.forEach(tour => {
      const category = tour.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(tour);
    });

    // Sort categories by priority
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      const priorityA = experiencePriority[a] || 99;
      const priorityB = experiencePriority[b] || 99;
      return priorityA - priorityB;
    });

    // Order tours within each category by location proximity
    const ordered: Tour[] = [];
    sortedCategories.forEach(category => {
      const categoryTours = grouped[category];
      if (categoryTours.length === 1) {
        ordered.push(categoryTours[0]);
      } else {
        // Use greedy algorithm for tours within same category
        const categoryOrdered: Tour[] = [categoryTours[0]];
        const remaining = [...categoryTours.slice(1)];

        while (remaining.length > 0) {
          const lastTour = categoryOrdered[categoryOrdered.length - 1];
          const lastLocation = lastTour.location;

          let closestIndex = 0;
          let closestDistance = Infinity;

          remaining.forEach((tour, index) => {
            const distance = this.estimateDistance(
              lastLocation.coordinates,
              tour.location.coordinates
            );
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = index;
            }
          });

          categoryOrdered.push(remaining[closestIndex]);
          remaining.splice(closestIndex, 1);
        }

        ordered.push(...categoryOrdered);
      }
    });

    return ordered;
  }

  /**
   * Estimate distance between two coordinates (Haversine formula)
   */
  private estimateDistance(
    coord1: { lat: number; lng: number },
    coord2: { lat: number; lng: number }
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRad(coord2.lat - coord1.lat);
    const dLon = this.toRad(coord2.lng - coord1.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(coord1.lat)) *
      Math.cos(this.toRad(coord2.lat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Calculate transfers between consecutive tours
   * Transfers are calculated per person and scale with participant count
   */
  private calculateTransfers(selectedTours: SelectedTour[]): Transfer[] {
    const transfers: Transfer[] = [];

    for (let i = 0; i < selectedTours.length - 1; i++) {
      const fromSelectedTour = selectedTours[i];
      const toSelectedTour = selectedTours[i + 1];
      const fromTour = fromSelectedTour.tour;
      const toTour = toSelectedTour.tour;

      // Use the number of participants from the "from" tour (they're the ones being transferred)
      const participants = fromSelectedTour.participants;

      const distance = this.estimateDistance(
        fromTour.location.coordinates,
        toTour.location.coordinates
      );

      // Estimate transfer time (assuming average speed of 60 km/h for road, 500 km/h for flights)
      let estimatedTime: number;
      let transferType: 'road' | 'flight' | 'boat' | 'other' = 'road';

      if (distance > 300) {
        transferType = 'flight';
        estimatedTime = Math.ceil(distance / 500); // Flights are faster
      } else if (distance > 100 && this.isNearWater(fromTour.location, toTour.location)) {
        transferType = 'boat';
        estimatedTime = Math.ceil(distance / 40); // Boats are slower
      } else {
        estimatedTime = Math.ceil(distance / 60); // Road transfers
      }

      // Calculate transfer price PER PERSON
      let pricePerPerson = 0;

      if (transferType === 'flight') {
        // Flight pricing: base cost + distance-based cost per person
        const baseCostPerPerson = 15000; // Base flight cost per person
        const distanceCostPerPerson = distance * 80; // 80 KES per km per person
        pricePerPerson = baseCostPerPerson + distanceCostPerPerson;
      } else if (transferType === 'boat') {
        // Boat pricing: distance-based per person
        pricePerPerson = distance * 120; // 120 KES per km per person
      } else {
        // Road transfer pricing: distance-based per person
        pricePerPerson = distance * this.TRANSFER_COST_PER_KM;
        // Add time-based cost for longer transfers (per person)
        if (estimatedTime > 4) {
          pricePerPerson += (estimatedTime - 4) * 500; // 500 KES per extra hour per person
        }
      }

      // Total price = price per person × number of participants
      const totalPrice = pricePerPerson * participants;

      transfers.push({
        from: {
          tourId: fromTour.id,
          location: fromTour.location.name,
          coordinates: fromTour.location.coordinates,
        },
        to: {
          tourId: toTour.id,
          location: toTour.location.name,
          coordinates: toTour.location.coordinates,
        },
        distance: Math.round(distance * 10) / 10,
        estimatedTime,
        transferType,
        price: Math.round(totalPrice),
        pricePerPerson: Math.round(pricePerPerson), // Store per-person price for display
        currency: 'KES',
        participants, // Store participant count for this transfer
        description: `${transferType === 'flight' ? 'Flight' : transferType === 'boat' ? 'Boat' : 'Road'} transfer from ${fromTour.location.name} to ${toTour.location.name} (${participants} person${participants > 1 ? 's' : ''})`,
      });
    }

    return transfers;
  }

  /**
   * Simple check if locations are near water (for boat transfers)
   * In production, use actual geographic data
   */
  private isNearWater(loc1: any, loc2: any): boolean {
    // Simplified - check if location names contain water-related terms
    const waterTerms = ['lake', 'coast', 'beach', 'island', 'marine'];
    const loc1Lower = loc1.name?.toLowerCase() || '';
    const loc2Lower = loc2.name?.toLowerCase() || '';
    return waterTerms.some(term => loc1Lower.includes(term) || loc2Lower.includes(term));
  }

  /**
   * Calculate total duration of the safari
   */
  private calculateTotalDuration(selectedTours: SelectedTour[]): number {
    let totalDays = 0;

    selectedTours.forEach(selectedTour => {
      // Parse duration string (e.g., "3 days", "5-7 days", "1 week")
      const duration = selectedTour.tour.duration;
      const daysMatch = duration.match(/(\d+)/);
      if (daysMatch) {
        const days = parseInt(daysMatch[1]);
        totalDays += days;
      }
    });

    // Add transfer days (if transfer takes more than 4 hours, count as half day)
    // This is simplified - can be more sophisticated
    return totalDays;
  }

  /**
   * Calculate complete pricing for the custom safari
   * All calculations are accurate and scale with participant count
   * Uses actual tour prices and currencies, converting to target currency
   */
  async calculatePricing(
    selectedTours: SelectedTour[],
    transfers: Transfer[],
    extras: any[] = [],
    targetCurrency: string = 'USD' // USD is now the default currency
  ): Promise<SafariPricing> {
    const breakdown: PricingBreakdown[] = [];
    const { currencyService } = await import('./currencyService');

    // Calculate tours subtotal (price × participants for each tour)
    // Convert each tour's price from its original currency to target currency
    let toursSubtotal = 0;
    for (const selectedTour of selectedTours) {
      // Get the tour's original price and currency
      const originalPrice = selectedTour.tour.price;
      const originalCurrency = selectedTour.tour.currency || 'USD'; // Default to USD if not specified

      // Convert to target currency
      const convertedUnitPrice = await currencyService.convert(
        originalPrice,
        originalCurrency,
        targetCurrency
      );

      // Price scales linearly with participants: 2 people = 2x price
      const tourPrice = convertedUnitPrice * selectedTour.participants;
      toursSubtotal += tourPrice;

      breakdown.push({
        item: selectedTour.tour.title,
        description: `${selectedTour.tour.duration} - ${selectedTour.participants} participant(s)`,
        quantity: selectedTour.participants,
        unitPrice: convertedUnitPrice,
        total: tourPrice,
        type: 'tour',
      });
    }

    // Calculate transfers subtotal (already includes participant count)
    // Transfers are in KES, convert to target currency
    let transfersSubtotal = 0;
    for (const transfer of transfers) {
      // Convert transfer prices from KES to target currency
      const convertedTransferPrice = await currencyService.convert(
        transfer.price,
        'KES',
        targetCurrency
      );
      const convertedTransferPricePerPerson = await currencyService.convert(
        transfer.pricePerPerson,
        'KES',
        targetCurrency
      );

      transfersSubtotal += convertedTransferPrice;
      breakdown.push({
        item: `Transfer: ${transfer.from.location} → ${transfer.to.location}`,
        description: `${transfer.distance}km, ${transfer.estimatedTime} hours (${transfer.transferType}) - ${transfer.participants} person${transfer.participants > 1 ? 's' : ''}`,
        quantity: transfer.participants,
        unitPrice: convertedTransferPricePerPerson,
        total: convertedTransferPrice,
        type: 'transfer',
      });
    }

    // Calculate accommodations subtotal (per tour, per night)
    let accommodationsSubtotal = 0;
    for (const selectedTour of selectedTours) {
      if (selectedTour.selectedAccommodation) {
        const acc = selectedTour.selectedAccommodation;
        // Calculate nights from tour duration
        const durationMatch = selectedTour.tour.duration.match(/(\d+)/);
        const nights = durationMatch ? parseInt(durationMatch[1]) : 1;

        // Convert accommodation price to target currency
        const convertedAccPrice = await currencyService.convert(
          acc.pricePerNight,
          acc.currency,
          targetCurrency
        );

        // Total accommodation cost = price per night × nights × participants
        // (assuming accommodation cost scales with participants)
        const accTotal = convertedAccPrice * nights * selectedTour.participants;
        accommodationsSubtotal += accTotal;

        breakdown.push({
          item: `Accommodation: ${acc.name}`,
          description: `${acc.type} - ${nights} night(s) for ${selectedTour.participants} participant(s)`,
          quantity: nights * selectedTour.participants,
          unitPrice: convertedAccPrice,
          total: accTotal,
          type: 'accommodation',
        });
      }
    }

    // Calculate extras subtotal (scale with total participants if applicable)
    let extrasSubtotal = 0;
    const totalParticipants = selectedTours.reduce((sum, tour) => sum + tour.participants, 0);
    extras.forEach(extra => {
      // Extras can be per-person or fixed - check if it has a perPerson flag
      const extraPrice = extra.perPerson ? extra.price * totalParticipants : extra.price;
      extrasSubtotal += extraPrice;
      breakdown.push({
        item: extra.name,
        description: extra.description || (extra.perPerson ? `${totalParticipants} person(s)` : 'Fixed fee'),
        quantity: extra.perPerson ? totalParticipants : 1,
        unitPrice: extra.price,
        total: extraPrice,
        type: 'extra',
      });
    });

    // Calculate subtotal (all already in target currency)
    const subtotal = toursSubtotal + transfersSubtotal + accommodationsSubtotal + extrasSubtotal;

    // Calculate taxes (16% VAT on subtotal) - already in target currency
    const taxes = Math.round(subtotal * this.TAX_RATE * 100) / 100; // Round to 2 decimal places
    breakdown.push({
      item: 'VAT (16%)',
      description: 'Value Added Tax',
      quantity: 1,
      unitPrice: taxes,
      total: taxes,
      type: 'tax',
    });

    // Service fee (fixed, not per person - administrative fee)
    // Convert service fee from KES to target currency
    const serviceFeeKES = this.SERVICE_FEE;
    const serviceFee = await currencyService.convert(serviceFeeKES, 'KES', targetCurrency);
    breakdown.push({
      item: 'Service Fee',
      description: 'Administrative and booking fee',
      quantity: 1,
      unitPrice: serviceFee,
      total: serviceFee,
      type: 'fee',
    });

    // Calculate total (subtotal + taxes + service fee) - all in target currency
    const total = subtotal + taxes + serviceFee;

    return {
      toursSubtotal: Math.round(toursSubtotal * 100) / 100,
      transfersSubtotal: Math.round(transfersSubtotal * 100) / 100,
      accommodationsSubtotal: Math.round(accommodationsSubtotal * 100) / 100,
      extrasSubtotal: Math.round(extrasSubtotal * 100) / 100,
      subtotal: Math.round(subtotal * 100) / 100,
      taxes: Math.round(taxes * 100) / 100,
      fees: Math.round(serviceFee * 100) / 100,
      total: Math.round(total * 100) / 100, // Round to 2 decimal places
      currency: targetCurrency,
      breakdown: breakdown.map(item => ({
        ...item,
        unitPrice: Math.round(item.unitPrice * 100) / 100,
        total: Math.round(item.total * 100) / 100,
      })),
    };
  }

  /**
   * Helper method to convert amount from KES to target currency
   */
  private async convertAmount(amount: number, targetCurrency: string): Promise<number> {
    if (targetCurrency === 'KES') {
      return amount;
    }
    try {
      const { currencyService } = await import('./currencyService');
      return await currencyService.convert(amount, 'KES', targetCurrency);
    } catch (error) {
      console.error('Currency conversion error:', error);
      return amount; // Fallback to original amount
    }
  }

  /**
   * Save custom safari to cart (database with user authentication)
   */
  /**
   * Save custom safari to cart (Local Storage)
   */
  async saveToCart(itinerary: CustomSafariItinerary, userId: string, notes?: string): Promise<string> {
    try {
      const cartItem: CustomSafariCartItem = {
        id: `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        itinerary: {
          ...itinerary,
          status: 'saved',
        },
        savedAt: new Date().toISOString(),
        notes,
      };

      const cart = this.getCartLocalStorage();
      cart.push(cartItem);
      localStorage.setItem('customSafariCart', JSON.stringify(cart));

      // Clear cache
      this.clearCartCache(userId);

      return cartItem.id;
    } catch (error) {
      console.error('Error saving to cart:', error);
      throw error;
    }
  }

  /**
   * Get all items in cart (Local Storage with filtering)
   */
  async getCart(userId?: string): Promise<CustomSafariCartItem[]> {
    try {
      // Return localStorage cart (shared for now, or could filter by userId if we stored it)
      // For now, we'll return all items since it's local to the browser
      return this.getCartLocalStorage();
    } catch (error) {
      console.error('Error getting cart:', error);
      return [];
    }
  }

  /**
   * Clear cart cache for a user (call after adding/removing items)
   */
  clearCartCache(userId?: string): void {
    if (userId) {
      this.cartCache.delete(userId);
    } else {
      this.cartCache.clear();
    }
  }

  /**
   * Get cart from localStorage (fallback)
   */
  getCartLocalStorage(): CustomSafariCartItem[] {
    try {
      const cartJson = localStorage.getItem('customSafariCart');
      return cartJson ? JSON.parse(cartJson) : [];
    } catch (error) {
      console.error('Error reading cart from localStorage:', error);
      return [];
    }
  }

  /**
   * Sync localStorage cart to database (Deprecated/Disabled)
   */
  async syncLocalStorageCartToDatabase(userId: string): Promise<void> {
    // Disabled for now as we are strictly using LocalStorage
    return;
  }

  /**
   * Remove item from cart
   */
  async removeFromCart(cartItemId: string, userId?: string): Promise<void> {
    try {
      const cart = this.getCartLocalStorage();
      const updatedCart = cart.filter(item => item.id !== cartItemId);
      localStorage.setItem('customSafariCart', JSON.stringify(updatedCart));

      this.clearCartCache(userId);
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }

  /**
   * Schedule a custom safari for specific dates (database)
   */
  /**
   * Schedule a custom safari for specific dates (Local Storage)
   */
  async scheduleSafari(
    itinerary: CustomSafariItinerary,
    startDate: string,
    participants: number,
    userId: string,
    cartItemId?: string,
    customerDetails?: any
  ): Promise<ScheduledSafari> {
    try {
      // Calculate end date based on total duration
      const start = new Date(startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + itinerary.totalDuration);

      const scheduled: ScheduledSafari = {
        id: `sched_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        user_id: userId, // Keep for type compatibility
        itinerary: {
          ...itinerary,
          startDate,
          endDate: end.toISOString().split('T')[0],
          status: 'scheduled',
        },
        scheduledStartDate: startDate,
        scheduledEndDate: end.toISOString().split('T')[0],
        participants,
        customerDetails,
        status: 'pending',
        createdAt: new Date().toISOString(),
      };

      // Save to "scheduledSafaris" in localStorage
      const existingStr = localStorage.getItem('scheduledSafaris');
      const existing: ScheduledSafari[] = existingStr ? JSON.parse(existingStr) : [];
      existing.push(scheduled);
      localStorage.setItem('scheduledSafaris', JSON.stringify(existing));

      // Update cart item status if cartItemId provided
      if (cartItemId) {
        const cart = this.getCartLocalStorage();
        const updatedCart = cart.map(item => {
          if (item.id === cartItemId) {
            return { ...item, itinerary: { ...item.itinerary, status: 'scheduled' } /*, scheduledDate: startDate */ }; // Update status
          }
          return item;
        });
        localStorage.setItem('customSafariCart', JSON.stringify(updatedCart));
      }

      return scheduled;
    } catch (error) {
      console.error('Error scheduling safari:', error);
      throw error;
    }
  }



  /**
   * Get all scheduled safaris (from database)
   */
  async getScheduledSafaris(userId?: string): Promise<ScheduledSafari[]> {
    try {
      const { supabase } = await import('./supabaseService');
      const { userAuthService } = await import('./userAuthService');

      // Get current user if not provided
      if (!userId) {
        const currentUser = await userAuthService.getCurrentUser();
        if (!currentUser) {
          return this.getScheduledSafarisLocalStorage();
        }
        userId = currentUser.id;
      }

      const { data, error } = await supabase
        .from('scheduled_safaris')
        .select('*')
        .eq('user_id', userId)
        .order('scheduled_start_date', { ascending: true });

      if (error) {
        console.error('Error fetching scheduled safaris:', error);
        return this.getScheduledSafarisLocalStorage();
      }

      return (data || []).map((item: any) => ({
        id: item.id,
        itinerary: item.itinerary as CustomSafariItinerary,
        scheduledStartDate: item.scheduled_start_date,
        scheduledEndDate: item.scheduled_end_date,
        participants: item.participants,
        customerDetails: item.customer_details,
        status: item.status,
        createdAt: item.created_at,
      }));
    } catch (error) {
      console.error('Error getting scheduled safaris:', error);
      return this.getScheduledSafarisLocalStorage();
    }
  }

  /**
   * Get scheduled safaris from localStorage (fallback)
   */
  private getScheduledSafarisLocalStorage(): ScheduledSafari[] {
    try {
      const scheduledJson = localStorage.getItem('scheduledSafaris');
      return scheduledJson ? JSON.parse(scheduledJson) : [];
    } catch (error) {
      console.error('Error reading scheduled safaris from localStorage:', error);
      return [];
    }
  }

  /**
   * Reorder tours in itinerary
   */
  async reorderTours(
    itinerary: CustomSafariItinerary,
    newOrder: string[],
    targetCurrency: string = 'USD'
  ): Promise<CustomSafariItinerary> {
    const tourMap = new Map(itinerary.tours.map(t => [t.tour.id, t]));
    const reorderedTours: SelectedTour[] = newOrder
      .map((tourId, index) => {
        const selectedTour = tourMap.get(tourId);
        if (selectedTour) {
          return { ...selectedTour, order: index + 1 };
        }
        return null;
      })
      .filter((t): t is SelectedTour => t !== null);

    // Recalculate transfers (with updated participant counts)
    const transfers = this.calculateTransfers(reorderedTours);

    // Recalculate duration
    const totalDuration = this.calculateTotalDuration(reorderedTours);

    // Recalculate pricing (async - includes currency conversion)
    const totalPrice = await this.calculatePricing(reorderedTours, transfers, [], targetCurrency);

    return {
      ...itinerary,
      tours: reorderedTours,
      transfers,
      totalDuration,
      totalPrice,
    };
  }

  /**
   * Update participants count for a tour in itinerary
   * Recalculates transfers and pricing to reflect new participant count
   */
  async updateParticipants(
    itinerary: CustomSafariItinerary,
    tourId: string,
    participants: number,
    targetCurrency: string = 'USD'
  ): Promise<CustomSafariItinerary> {
    // Ensure participants is at least 1
    const validParticipants = Math.max(1, participants);

    const updatedTours = itinerary.tours.map(t =>
      t.tour.id === tourId ? { ...t, participants: validParticipants } : t
    );

    // Recalculate transfers (they scale with participants)
    const transfers = this.calculateTransfers(updatedTours);

    // Recalculate pricing (async - includes currency conversion)
    const totalPrice = await this.calculatePricing(updatedTours, transfers, [], targetCurrency);

    return {
      ...itinerary,
      tours: updatedTours,
      transfers, // Updated transfers with new participant counts
      totalPrice,
    };
  }

  /**
   * Update start date for the safari
   */
  async updateStartDate(itinerary: CustomSafariItinerary, startDate: string): Promise<CustomSafariItinerary> {
    const startNum = new Date(startDate).getTime();
    if (isNaN(startNum)) return itinerary;

    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + itinerary.totalDuration);

    return {
      ...itinerary,
      startDate,
      endDate: end.toISOString().split('T')[0],
      status: 'scheduled'
    };
  }
}

export const customSafariService = new CustomSafariService();

